/**
 * The one place this app reads build-time env.
 *
 * Every key carries the `VITE_` prefix, matching `vite.config.ts`'s
 * `envPrefix`. A bare key is not inlined into the browser bundle at all — that
 * is how jerry's `CMS_API_URL` reached the build and never reached the client
 * (JERY-124), producing a console that failed only in production.
 */

import type { FinixCardConfig } from "@pirouz-ca/payments-core";

/**
 * Gateway base path for this app's two backends: `/api/v1/ordering/*`
 * (paio-ordering, port 8023) and `/api/v1/commerce/*` (paio-commerce).
 *
 * Same-origin by default. In dev, `vite.config.ts` proxies `/api/v1` so the
 * browser stays same-origin and the first-party `paio_sid` cookie rides along;
 * in production the deployed origin must be on the gateway's CORS allowlist,
 * which it is NOT today — see GATE-421.
 */
export const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

/** Where the ordering service's flow resolver lives. */
export const ORDERING_API: string = `${API_BASE}/ordering`;

/**
 * The app's OWN hosts (ORDR-164): where it is served as itself, for staff and
 * for `?site=` / `?brand=` links. On any OTHER host the app asks
 * `flow/?entry=BRAND_DOMAIN&host=` which brand that host belongs to, and NEVER
 * on these: the staff hosts are not brands, and asking would spend the public
 * brand throttle on every kitchen reload.
 *
 * Explicit config rather than a guess from the hostname's shape. A comma list
 * in `VITE_STAFF_HOSTS`, declared in `.do/app-ordering.yaml` and graded in the
 * bundle by `scripts/assert-bundle-env.mjs`.
 */
export const DEFAULT_STAFF_HOSTS = "ordering.pirouz.ai,localhost,127.0.0.1";

/** The staff host list, trimmed and lower-cased; the default when unset or blank. */
export function staffHostsFrom(raw: string | undefined): readonly string[] {
  const value = raw === undefined || raw.trim() === "" ? DEFAULT_STAFF_HOSTS : raw;
  return value
    .split(",")
    .map((host) => host.trim().toLowerCase())
    .filter((host) => host !== "");
}

/** Exact match after case and a trailing dot; never a suffix match. */
export function isStaffHost(hostname: string, staffHosts: readonly string[]): boolean {
  const host = hostname.trim().toLowerCase().replace(/\.$/, "");
  return staffHosts.includes(host);
}

// The fallback repeats DEFAULT_STAFF_HOSTS as a literal, the shape the bundle
// grader's LITERAL rule reads (see `RULES` in scripts/assert-bundle-env.mjs).
// `doSpec.test.ts` holds the spec value, this literal's twin, equal to it.
export const STAFF_HOSTS: readonly string[] = staffHostsFrom(
  import.meta.env.VITE_STAFF_HOSTS ?? "ordering.pirouz.ai,localhost,127.0.0.1",
);

/**
 * The build switch for "Onboard restaurant" (ORDR-190).
 *
 * The screen calls paio-onboarding's setup endpoints, which do not exist yet, so
 * a build without this switch must not show reseller admins a flow that fails on
 * its first request. OFF unless the build sets `VITE_ONBOARDING_ENABLED` to the
 * exact literal `"true"`; a near miss (`"TRUE"`, `"1"`, `" true"`) stays off.
 * HQ declares the value in `.do/app-ordering.yaml` when the endpoints ship.
 */
export function onboardingEnabledFrom(raw: string | undefined): boolean {
  return raw === "true";
}

export const ONBOARDING_ENABLED: boolean = onboardingEnabledFrom(import.meta.env.VITE_ONBOARDING_ENABLED);

/**
 * The currency a diner's FOOD cart is opened in (ORDR-42). paio-commerce needs
 * one on cart creation and no anonymous route states a venue's; every line is
 * then validated against it server-side, so a wrong value is refused, never
 * silently charged.
 */
export const ORDERING_CURRENCY: string = import.meta.env.VITE_ORDERING_CURRENCY ?? "CAD";

/** Where checkout and payment live. */
export const COMMERCE_API: string = `${API_BASE}/commerce`;

/**
 * The PLATFORM half of Finix hosted card fields (ORDR-55, ORDR-57), from build
 * env only.
 *
 * - `VITE_FINIX_ENVIRONMENT`: `sandbox` (staging) or `live`.
 * - `VITE_FINIX_APPLICATION_ID`: the Finix application the hosted form mints for.
 *
 * Both are platform-wide and required. Any one missing, blank or unrecognised
 * gives `null`, and the diner is told payment is not connected: no SDK is loaded
 * and no token is ever invented. Neither is a secret.
 *
 * THE MERCHANT IS NOT HERE. ordering.pirouz.ai serves many firms with one
 * merchant each, so a merchant id in the build would open every diner's fraud
 * session against one firm. It comes from the server per location
 * (`customer/menuPayment.ts`) and is joined to this in `cardConfigFor`.
 */
export interface FinixCardPlatform {
  readonly environment: "sandbox" | "live";
  readonly applicationId: string;
}

export function cardPlatformFromEnv(env: Readonly<Record<string, string | undefined>>): FinixCardPlatform | null {
  const environment = env["VITE_FINIX_ENVIRONMENT"]?.trim();
  const applicationId = env["VITE_FINIX_APPLICATION_ID"]?.trim();
  if ((environment !== "sandbox" && environment !== "live") || !applicationId) {
    return null;
  }
  return { environment, applicationId };
}

/** The card config for one location: the platform plus that firm's merchant. */
export function cardConfigFor(platform: FinixCardPlatform, merchantId: string): FinixCardConfig {
  return {
    environment: platform.environment,
    applicationId: platform.applicationId,
    merchantId,
    formOptions: {
      // Card only: the default also offers a bank tab that loads Plaid, which
      // is neither in scope nor in the CSP this app documents.
      paymentMethods: ["card"],
      // The hosted form defaults to USA, whose formatter reduces a Canadian
      // postal code to its digits (payments-core README).
      defaultValues: { address_country: "CAN" },
    },
  };
}

export const CARD_PLATFORM: FinixCardPlatform | null = cardPlatformFromEnv(import.meta.env);
